"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader as Loader2, Send, CircleCheck as CheckCircle } from 'lucide-react';
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-center size-16 rounded-lg bg-primary/20 text-primary">
          <CheckCircle className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-foreground">
            Message Received
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Your phone number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you?" className="min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="captcha" rules={{
      required: "CAPTCHA verification is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Spam Protection</FormLabel>
            <FormControl>
              <Input placeholder="Type 'human' to verify" {...field} />
            </FormControl>
            <FormDescription>
              Please enter the word 'human' to prove you are not a robot.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300" size="lg">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Sending Message...
          </> : <>
            <Send className="mr-2 size-5" />
            Send Message
          </>}
      </Button>
    </Form>;
}
export default function ContactForm() {
  return <section id="contact-form" className="w-full bg-background py-20 md:py-32 px-4 md:px-8">
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground">
            Get in Touch
          </h2>
        </div>

        <Card className="shadow-lg border-border/50">
          <CardContent className="p-6 md:p-10">
            <FormProvider formId="contact_form_feelgoodworks" sectionName="Contact Form" defaultValues={{
            name: "",
            phone: "",
            email: "",
            message: "",
            captcha: ""
          }} submitText="Send Message" successMessage="Thank you for reaching out to Feel Good Works! We have received your message and will get back to you shortly.">
              <ContactFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}